import { CircularProgress } from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, RotateCcw } from 'lucide-react'
import { useContext, useEffect, useState } from 'react'
import { toast } from 'sonner'
import Page from '~/components/Page'
import { AuthorizedAppButton } from '~/components/new/buttons/authorized-button'
import { ADMINISTRATION_EDIT, ADMINISTRATION_READ } from '~/components/routes/visioReviews/tabs'
import {
  DEFAULT_THRESHOLDS,
  kioskTypeLabel,
  VISIO_REVIEWS_STALE_TIME,
} from '~/components/routes/visioReviews/visioReviews.utils'
import { Button } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import { Textarea } from '~/components/ui/textarea'
import { api } from '~/lib/tuyau'
import { AuthContext } from '~/utils/context/auth.context'
import { withRoutePermissions } from '~/utils/rbac'

export const Route = createFileRoute('/_authenticated/visio-reviews/admin')({
  staticData: {
    permission: ADMINISTRATION_READ,
  },
  component: VisioReviewsAdminPage,
})
withRoutePermissions(Route)

function VisioReviewsAdminPage() {
  const navigate = Route.useNavigate()
  const { isLoading } = useContext(AuthContext)

  const { data: config } = useQuery(
    api.visioReviews.getConfig.queryOptions({}, { staleTime: VISIO_REVIEWS_STALE_TIME }),
  )
  const { mutate: updateConfig, isPending: isSavingConfig } = useMutation(
    api.visioReviews.updateConfig.mutationOptions({
      meta: { invalidates: [api.visioReviews.getConfig.queryKey()] },
    }),
  )

  const [problemPrompt, setProblemPrompt] = useState('')
  const [openPrompt, setOpenPrompt] = useState('')
  const [lowThreshold, setLowThreshold] = useState(DEFAULT_THRESHOLDS.low)
  const [highThreshold, setHighThreshold] = useState(DEFAULT_THRESHOLDS.high)

  useEffect(() => {
    if (config) {
      setProblemPrompt(config.problemPrompt ?? '')
      setOpenPrompt(config.openPrompt ?? '')
      setLowThreshold(config.lowGradeThreshold ?? DEFAULT_THRESHOLDS.low)
      setHighThreshold(config.highGradeThreshold ?? DEFAULT_THRESHOLDS.high)
    }
  }, [config])

  const handleSavePrompts = () => {
    updateConfig({
      body: {
        problemPrompt: problemPrompt || null,
        openPrompt: openPrompt || null,
      },
    })
  }

  const handleSaveThresholds = () => {
    if (lowThreshold >= highThreshold) {
      toast.error('Le seuil faible doit être inférieur au seuil excellent')
      return
    }
    updateConfig({
      body: {
        lowGradeThreshold: lowThreshold,
        highGradeThreshold: highThreshold,
      },
    })
  }

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sidebar px-6 py-10">
        <CircularProgress />
      </main>
    )
  }

  return (
    <Page>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Administration — Contrôle Qualité</h1>
          <Button variant="outline" onClick={() => navigate({ to: '/visio-reviews' })}>
            <ArrowLeft className="h-4 w-4" />
            Retour au dashboard
          </Button>
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <div className="space-y-4">
              <div>
                <h3 className="mb-2 font-semibold">Critères d'évaluation</h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  Définissez les critères de notation utilisés par l'IA pour évaluer les opérateurs.
                  Ces critères seront insérés dans le prompt d'évaluation de l'IA.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">
                    Critères — {kioskTypeLabel('CHECK_OUT')}
                    <span className="text-muted-foreground ml-1 font-normal">
                      (borne de paiement)
                    </span>
                  </label>
                  <AuthorizedAppButton
                    permission={ADMINISTRATION_EDIT}
                    variant="outline"
                    size="sm"
                    disabled={isSavingConfig}
                    onClick={() => updateConfig({ body: { problemPrompt: null } })}
                  >
                    <RotateCcw className="h-3 w-3" />
                    Réinitialiser
                  </AuthorizedAppButton>
                </div>
                <Textarea
                  value={problemPrompt}
                  onChange={(e) => setProblemPrompt(e.target.value)}
                  rows={8}
                  className="font-mono text-sm"
                  placeholder="Critères de notation pour les interactions liées à un problème client..."
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">
                    Critères — {kioskTypeLabel('CHECK_IN')}
                    <span className="text-muted-foreground ml-1 font-normal">
                      (kiosque d'entrée)
                    </span>
                  </label>
                  <AuthorizedAppButton
                    permission={ADMINISTRATION_EDIT}
                    variant="outline"
                    size="sm"
                    disabled={isSavingConfig}
                    onClick={() => updateConfig({ body: { openPrompt: null } })}
                  >
                    <RotateCcw className="h-3 w-3" />
                    Réinitialiser
                  </AuthorizedAppButton>
                </div>
                <Textarea
                  value={openPrompt}
                  onChange={(e) => setOpenPrompt(e.target.value)}
                  rows={8}
                  className="font-mono text-sm"
                  placeholder="Critères de notation pour les interactions d'ouverture de porte..."
                />
              </div>

              <div className="flex gap-3">
                <AuthorizedAppButton
                  permission={ADMINISTRATION_EDIT}
                  onClick={handleSavePrompts}
                  disabled={isSavingConfig}
                >
                  {isSavingConfig ? 'Enregistrement...' : 'Enregistrer les critères'}
                </AuthorizedAppButton>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="mb-4 font-semibold">Paramètres d'Analyse</h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm">Seuil note faible (alerte)</label>
                <Input
                  type="number"
                  value={lowThreshold}
                  onChange={(e) => setLowThreshold(Number(e.target.value))}
                  min={1}
                  max={10}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm">Seuil note excellente</label>
                <Input
                  type="number"
                  value={highThreshold}
                  onChange={(e) => setHighThreshold(Number(e.target.value))}
                  min={1}
                  max={10}
                />
              </div>
            </div>
            <div className="mt-4">
              <AuthorizedAppButton
                permission={ADMINISTRATION_EDIT}
                onClick={handleSaveThresholds}
                disabled={isSavingConfig}
              >
                {isSavingConfig ? 'Enregistrement...' : 'Enregistrer les paramètres'}
              </AuthorizedAppButton>
            </div>
          </Card>
        </div>
      </div>
    </Page>
  )
}
